import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Cloud as CloudIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { listTrails } from '../../services/cloudtrailApi';

const statusColors = {
  completed: { bg: '#E8F5E9', color: '#2E7D32' },
  in_progress: { bg: '#E3F2FD', color: '#1565C0' },
  partial_failure: { bg: '#FFF3E0', color: '#E65100' },
  failed: { bg: '#FFEBEE', color: '#C62828' },
  pending: { bg: '#F5F5F5', color: '#616161' },
  deleting: { bg: '#E3F2FD', color: '#1565C0' },
  updating: { bg: '#E3F2FD', color: '#1565C0' },
  deleted: { bg: '#F5F5F5', color: '#616161' },
};

const statusLabels = {
  completed: 'Completed',
  in_progress: 'In Progress',
  partial_failure: 'Partial Failure',
  failed: 'Failed',
  pending: 'Pending',
  deleting: 'Deleting',
  updating: 'Updating',
  deleted: 'Deleted',
};

const activeTrailStatuses = ['in_progress', 'deleting', 'updating'];

const TrailList = () => {
  const navigate = useNavigate();
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchTrails = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await listTrails();
      const data = response.data?.trails || [];
      setTrails(data);
      setError(null);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await listTrails();
      setTrails(response.data?.trails || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrails();
  }, [fetchTrails]);

  // Auto-refresh polling when any trail has an active status
  useEffect(() => {
    if (trails.length === 0) return;
    const hasActive = trails.some((t) => activeTrailStatuses.includes(t.status));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const data = await fetchTrails(false);
        const stillActive = data.some((t) => activeTrailStatuses.includes(t.status));
        if (!stillActive && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [trails, fetchTrails]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (trails.length === 0) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            Trails
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}
          >
            Manage AWS CloudTrail trails across your organization's accounts
          </Typography>
        </Box>
        </motion.div>

        <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <CloudIcon sx={{ fontSize: 40, color: '#4698D3' }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1.5,
            }}
          >
            No Trails Yet
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              maxWidth: 480,
              margin: '0 auto',
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            Create your first CloudTrail trail to start logging AWS API activity across your
            organization's accounts.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trails/create')}
            sx={{
              backgroundColor: '#4698D3',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              px: 3,
              '&:hover': { backgroundColor: '#3A7BB0' },
            }}
          >
            Create Trail
          </Button>
        </Paper>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: { xs: 3, sm: 4 },
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            Trails
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}
          >
            {trails.length} trail{trails.length !== 1 ? 's' : ''} configured
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={
              refreshing
                ? <CircularProgress size={16} sx={{ color: '#4698D3' }} />
                : <RefreshIcon />
            }
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              borderColor: '#E5E5E5',
              color: '#253E66',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              px: 2.5,
              '&:hover': { borderColor: '#4698D3', backgroundColor: 'rgba(70, 152, 211, 0.04)' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trails/create')}
            sx={{
              backgroundColor: '#4698D3',
              textTransform: 'none',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              borderRadius: '6px',
              px: 3,
              '&:hover': { backgroundColor: '#3A7BB0' },
            }}
          >
            Create Trail
          </Button>
        </Box>
      </Box>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
              {['Trail Name', 'Status', 'S3 Bucket', 'Multi-Region', 'Accounts', 'Created By', 'Created At'].map(
                (header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      color: '#253E66',
                      fontSize: '0.8rem',
                      borderBottom: '2px solid #E5E5E5',
                    }}
                  >
                    {header}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {trails.map((trail, index) => {
              const sc = statusColors[trail.status] || statusColors.pending;
              return (
                <TableRow
                  key={trail.id}
                  hover
                  onClick={() => navigate(`/trails/${trail.id}`)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#F9FBFD' },
                    '@keyframes fadeInRow': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'none' },
                    },
                    animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      color: '#253E66',
                    }}
                  >
                    {trail.name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[trail.status] || trail.status}
                      size="small"
                      sx={{
                        backgroundColor: sc.bg,
                        color: sc.color,
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666' }}>
                    {trail.s3BucketName}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666' }}>
                    {trail.isMultiRegion ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666' }}>
                    {trail.successCount}/{trail.accountCount} succeeded
                    {trail.failedCount > 0 && (
                      <Typography
                        component="span"
                        sx={{ color: '#C62828', fontSize: '0.8rem', ml: 0.5 }}
                      >
                        ({trail.failedCount} failed)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666' }}>
                    {trail.createdByEmail}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666' }}>
                    {new Date(trail.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </motion.div>
    </motion.div>
  );
};

export default TrailList;
